import { useState, useEffect, useCallback, useMemo } from "react";
import { useApi, useAuth } from "./AuthContext";
import {
  Briefcase, Plus, X, ChevronLeft, ChevronRight, DollarSign, Phone,
  MessageSquare, User, Calendar as CalendarIcon, Clock, Trash2, AlertTriangle,
  Loader2, UserCog, Save, CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import { formatTime12h, formatLogTime } from "../utils/time";
import TimeInput12h from "./TimeInput12h";
import BackButton from "./BackButton";
import CurrencyInput from "./CurrencyInput";
import { formatUSD, formatUSDCompact } from "../utils/currency";

const STATUS_OPTIONS = ["Lead", "Site Visit", "Proposal Sent", "Negotiation", "Won", "Lost"];
const STATUS_COLORS = {
  Lead: "bg-stone-100 text-stone-700",
  "Site Visit": "bg-amber-50 text-amber-700",
  "Proposal Sent": "bg-blue-50 text-blue-700",
  Negotiation: "bg-purple-50 text-purple-700",
  Won: "bg-green-50 text-green-700",
  Lost: "bg-red-50 text-red-600",
};

const formatYYYYMM = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const formatYYYYMMDD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const sameDayString = (dateStr, ref) => dateStr === formatYYYYMMDD(ref);

function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  const startOffset = first.getDay(); // 0 = Sun
  const cells = [];
  // leading prev-month
  for (let i = 0; i < startOffset; i++) {
    const d = new Date(year, month, 1 - (startOffset - i));
    cells.push({ date: d, inMonth: false });
  }
  // current month
  const lastDay = new Date(year, month + 1, 0).getDate();
  for (let i = 1; i <= lastDay; i++) {
    cells.push({ date: new Date(year, month, i), inMonth: true });
  }
  // trailing to fill grid (multiple of 7)
  while (cells.length % 7) {
    const last = cells[cells.length - 1].date;
    const d = new Date(last); d.setDate(d.getDate() + 1);
    cells.push({ date: d, inMonth: false });
  }
  return cells;
}

export default function SalesPipelineModule({ readOnly = false }) {
  const api = useApi();
  const { user: authUser, isAdmin } = useAuth();

  const [cursor, setCursor] = useState(() => new Date());
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [draggingDeal, setDraggingDeal] = useState(null);
  const [confirmReschedule, setConfirmReschedule] = useState(null);
  const [newNote, setNewNote] = useState("");
  // Staged edits for the Update button (Pipeline Stage / Next Action Date+Time / Responsible Manager)
  const [editForm, setEditForm] = useState({ status: "", next_action_date: "", next_action_time: "", responsible_user_id: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const emptyForm = {
    prospect_name: "", phone: "", email: "", address: "", company_name: "",
    amount: 0, status: "Lead",
    next_action_date: formatYYYYMMDD(new Date()),
    next_action_time: "09:00",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);

  const monthLabel = useMemo(() =>
    cursor.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    [cursor]
  );
  const monthKey = useMemo(() => formatYYYYMM(cursor), [cursor]);
  const grid = useMemo(() => buildMonthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/sales/deals?month=${monthKey}`);
      setDeals(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to load deals");
    } finally { setLoading(false); }
  }, [api, monthKey]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/sales/users");
      setUsers(data || []);
    } catch (_e) { setUsers([]); }
  }, [api]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);
  useEffect(() => { if (isAdmin) fetchUsers(); }, [isAdmin, fetchUsers]);

  const dealsByDay = useMemo(() => {
    const map = {};
    deals.forEach(d => {
      if (!d.next_action_date) return; // backlog handled separately
      const k = d.next_action_date;
      if (!map[k]) map[k] = [];
      map[k].push(d);
    });
    Object.values(map).forEach(arr => arr.sort((a, b) => (a.next_action_time || "").localeCompare(b.next_action_time || "")));
    return map;
  }, [deals]);

  // Backlog: deals with empty/missing next_action_date.
  const backlogDeals = useMemo(
    () => deals.filter(d => !d.next_action_date),
    [deals]
  );

  const goPrev = () => setCursor(c => { const d = new Date(c); d.setMonth(d.getMonth() - 1); return d; });
  const goNext = () => setCursor(c => { const d = new Date(c); d.setMonth(d.getMonth() + 1); return d; });
  const goToday = () => setCursor(new Date());

  const openNew = (date) => {
    setForm({ ...emptyForm, next_action_date: date ? formatYYYYMMDD(date) : formatYYYYMMDD(new Date()) });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    if (!form.prospect_name.trim()) { toast.error("Prospect name required"); return; }
    try {
      const payload = { ...form, amount: Number(form.amount || 0) };
      await api.post("/admin/sales/deals", payload);
      toast.success("Deal created");
      setShowForm(false); setForm(emptyForm);
      fetchDeals();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create deal");
    }
  };

  const onDragStart = (e, deal) => {
    if (readOnly) return;
    setDraggingDeal(deal);
    try { e.dataTransfer.setData("text/plain", deal.id); } catch (_e) { /* */ }
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => setDraggingDeal(null);
  const onDayDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDayDrop = (e, dateStr) => {
    e.preventDefault();
    const d = draggingDeal;
    setDraggingDeal(null);
    if (!d || d.next_action_date === dateStr) return;
    setConfirmReschedule({ deal: d, newDate: dateStr });
  };

  const confirmRescheduleSave = async () => {
    if (!confirmReschedule) return;
    const { deal, newDate } = confirmReschedule;
    try {
      await api.put(`/admin/sales/deals/${deal.id}/reschedule`, { next_action_date: newDate });
      toast.success(`Rescheduled to ${newDate}`);
      setConfirmReschedule(null);
      fetchDeals();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to reschedule");
    }
  };

  const updateDealField = async (field, value) => {
    if (!selectedDeal) return;
    try {
      const { data } = await api.put(`/admin/sales/deals/${selectedDeal.id}`, { [field]: value });
      setSelectedDeal(data);
      fetchDeals();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    }
  };

  // Sync the staged edit form whenever a different deal is selected.
  useEffect(() => {
    if (selectedDeal) {
      setEditForm({
        status: selectedDeal.status || "Lead",
        next_action_date: selectedDeal.next_action_date || "",
        next_action_time: selectedDeal.next_action_time || "",
        responsible_user_id: selectedDeal.responsible_user_id || "",
      });
    }
  }, [selectedDeal]);

  const isDirty = useMemo(() => {
    if (!selectedDeal) return false;
    return (
      editForm.status !== (selectedDeal.status || "Lead") ||
      editForm.next_action_date !== (selectedDeal.next_action_date || "") ||
      editForm.next_action_time !== (selectedDeal.next_action_time || "") ||
      editForm.responsible_user_id !== (selectedDeal.responsible_user_id || "")
    );
  }, [editForm, selectedDeal]);

  const saveEdits = async () => {
    if (!selectedDeal || !isDirty || savingEdit) return;
    setSavingEdit(true);
    try {
      // 1) Reassign first (admin-only). If responsible changed and user is admin, fire assign endpoint.
      if (isAdmin && editForm.responsible_user_id && editForm.responsible_user_id !== selectedDeal.responsible_user_id) {
        await api.put(`/admin/sales/deals/${selectedDeal.id}/assign`, { responsible_user_id: editForm.responsible_user_id });
      }
      // 2) Bundle the rest (status + next_action_date/time) into a single PUT.
      const payload = {};
      if (editForm.status !== selectedDeal.status) payload.status = editForm.status;
      if (editForm.next_action_date !== (selectedDeal.next_action_date || "")) payload.next_action_date = editForm.next_action_date;
      if (editForm.next_action_time !== (selectedDeal.next_action_time || "")) payload.next_action_time = editForm.next_action_time;
      let updated = selectedDeal;
      if (Object.keys(payload).length) {
        const { data } = await api.put(`/admin/sales/deals/${selectedDeal.id}`, payload);
        updated = data;
      } else {
        // refetch latest after assign
        const { data } = await api.get(`/admin/sales/deals/${selectedDeal.id}`);
        updated = data;
      }
      setSelectedDeal(updated);
      fetchDeals();
      toast.success("Changes saved");
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Update failed");
    } finally { setSavingEdit(false); }
  };

  const reassignDeal = async (newUserId) => {
    if (!selectedDeal || !isAdmin) return;
    try {
      const { data } = await api.put(`/admin/sales/deals/${selectedDeal.id}/assign`, { responsible_user_id: newUserId });
      setSelectedDeal(data);
      toast.success("Responsible Manager updated");
      fetchDeals();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Reassign failed");
    }
  };

  const addNote = async () => {
    if (!selectedDeal || !newNote.trim()) return;
    const text = newNote.trim();
    try {
      const { data } = await api.post(`/admin/sales/deals/${selectedDeal.id}/notes`, { text });
      // Backend returns the full deal with history. Defensive: if response is missing,
      // refetch from /sales/deals/{id} to ensure UI reflects DB state.
      let next = data;
      if (!next || !Array.isArray(next.history)) {
        const refreshed = await api.get(`/admin/sales/deals/${selectedDeal.id}`);
        next = refreshed.data;
      }
      setSelectedDeal(next);
      setNewNote("");
      toast.success("Note added");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add note");
    }
  };

  const deleteDeal = async () => {
    if (!selectedDeal) return;
    if (!window.confirm(`Delete deal "${selectedDeal.prospect_name}"?`)) return;
    try {
      await api.delete(`/admin/sales/deals/${selectedDeal.id}`);
      toast.success("Deal deleted");
      setSelectedDeal(null);
      fetchDeals();
    } catch (err) { toast.error("Failed to delete"); }
  };

  // ====================== DEAL DETAIL VIEW ======================
  if (selectedDeal) {
    const history = selectedDeal.history || [];
    return (
      <div data-testid="deal-detail" className="space-y-3 max-w-5xl">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-forest-600" /> {selectedDeal.prospect_name}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedDeal.status] || ""}`}>{selectedDeal.status}</span>
          </h2>
          {!readOnly && (
            <button data-testid="deal-delete-btn" onClick={deleteDeal} className="text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          )}
        </div>
        <BackButton onClick={() => setSelectedDeal(null)} testId="deal-detail-back" label="Back to calendar" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* LEFT: Info + Actions + Timeline */}
          <div className="lg:col-span-2 space-y-4">
            {/* Prospect Info */}
            <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Prospect Info</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-stone-400">Phone:</span> <span className="font-medium">{selectedDeal.phone || "—"}</span></div>
                <div><span className="text-stone-400">Email:</span> <span className="font-medium">{selectedDeal.email || "—"}</span></div>
                <div><span className="text-stone-400">Address:</span> <span className="font-medium">{selectedDeal.address || "—"}</span></div>
                <div><span className="text-stone-400">Company:</span> <span className="font-medium">{selectedDeal.company_name || "—"}</span></div>
                <div><span className="text-stone-400">Amount:</span> <span className="font-bold text-forest-700">{formatUSD(selectedDeal.amount || 0)}</span></div>
                <div><span className="text-stone-400">Saved next action:</span> <span className="font-medium">{selectedDeal.next_action_date} {formatTime12h(selectedDeal.next_action_time)}</span></div>
              </div>
              <div className="flex items-center gap-2 pt-2 flex-wrap">
                {selectedDeal.phone && (
                  <a href={`tel:${selectedDeal.phone}`} data-testid="deal-call-btn" className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                    <Phone className="w-4 h-4" /> Call
                  </a>
                )}
                {selectedDeal.phone && (
                  <a href={`sms:${selectedDeal.phone}`} data-testid="deal-sms-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                    <MessageSquare className="w-4 h-4" /> Send Text
                  </a>
                )}
              </div>
            </div>

            {/* Editable bundle: Pipeline Stage + Next Action + (Manager when admin) + Update */}
            {!readOnly && (
              <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
                <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Pipeline Stage & Next Action</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Pipeline Stage</label>
                    <select
                      data-testid="deal-status-select"
                      value={editForm.status}
                      onChange={(e) => setEditForm(f => ({ ...f, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500"
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-500 mb-1">Next Action Date</label>
                    <input
                      data-testid="deal-next-date"
                      type="date"
                      value={editForm.next_action_date}
                      onChange={(e) => setEditForm(f => ({ ...f, next_action_date: e.target.value }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-medium text-stone-500 mb-1">Next Action Time</label>
                    <TimeInput12h value={editForm.next_action_time} onChange={(v) => setEditForm(f => ({ ...f, next_action_time: v }))} testId="deal-next-time" />
                  </div>
                </div>

                {selectedDeal.linked_client_id && (
                  <p className="text-xs text-green-700 bg-green-50 px-3 py-1.5 rounded-lg inline-block">
                    Linked client created. Responsible Manager preserved.
                  </p>
                )}

                {/* UPDATE BUTTON — large, full-width, with success flash */}
                <button
                  data-testid="deal-update-btn"
                  onClick={saveEdits}
                  disabled={!isDirty || savingEdit}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold shadow-sm transition-all ${
                    savedFlash
                      ? "bg-green-600 text-white"
                      : isDirty && !savingEdit
                        ? "bg-forest-600 hover:bg-forest-700 text-white"
                        : "bg-stone-200 text-stone-500 cursor-not-allowed"
                  }`}
                >
                  {savingEdit ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                  ) : savedFlash ? (
                    <><CheckCircle2 className="w-4 h-4" /> Saved</>
                  ) : (
                    <><Save className="w-4 h-4" /> Update</>
                  )}
                </button>
              </div>
            )}

            {/* Interactions Timeline */}
            <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider">Interactions Timeline</h3>
              {!readOnly && (
                <div className="flex gap-2">
                  <input
                    data-testid="deal-note-input"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addNote(); } }}
                    placeholder="Add a note (call summary, follow-up, etc.)"
                    className="flex-1 px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500"
                  />
                  <button data-testid="deal-note-add-btn" onClick={addNote} disabled={!newNote.trim()} className="bg-forest-600 hover:bg-forest-700 disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-5 py-2 rounded-lg text-sm font-semibold">Add</button>
                </div>
              )}
              {history.length === 0 ? (
                <p className="text-sm text-stone-400 py-6 text-center" data-testid="timeline-empty">No interactions yet</p>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="timeline-list">
                  {history.map(h => (
                    <div key={h.id} data-testid={`timeline-entry-${h.id}`} className="border-l-2 border-forest-400 pl-3 py-1.5 bg-stone-50/40 rounded-r">
                      <p className="text-sm text-stone-700 whitespace-pre-wrap">{h.text}</p>
                      <p className="text-xs text-stone-400 mt-0.5 flex items-center gap-2">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {formatLogTime(h.created_at)}</span>
                        <span>·</span>
                        <span className="font-medium text-stone-500">{h.created_by || "Unknown"}</span>
                        {h.type === "reschedule" && <span className="px-1.5 py-0 rounded text-[10px] bg-amber-50 text-amber-700">reschedule</span>}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Manager & Meta */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-3">
              <h3 className="font-semibold text-stone-700 text-sm uppercase tracking-wider flex items-center gap-2">
                <UserCog className="w-4 h-4" /> Responsible Manager
              </h3>
              {isAdmin ? (
                <select
                  data-testid="deal-assign-select"
                  value={editForm.responsible_user_id || ""}
                  onChange={(e) => setEditForm(f => ({ ...f, responsible_user_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-forest-500"
                >
                  {users.map(u => <option key={u.id} value={u.id}>{u.name} {u.role ? `(${u.role})` : ""}</option>)}
                </select>
              ) : (
                <p className="text-sm font-medium text-stone-700">{selectedDeal.responsible_user_name || "—"}</p>
              )}
              <p className="text-xs text-stone-400">Created {formatLogTime(selectedDeal.created_at)}</p>
              {isAdmin && !readOnly && (
                <button
                  data-testid="deal-update-btn-side"
                  onClick={saveEdits}
                  disabled={!isDirty || savingEdit}
                  className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                    savedFlash
                      ? "bg-green-600 text-white"
                      : isDirty && !savingEdit
                        ? "bg-forest-600 hover:bg-forest-700 text-white"
                        : "bg-stone-200 text-stone-500 cursor-not-allowed"
                  }`}
                >
                  {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {savingEdit ? "Saving…" : savedFlash ? "Saved" : "Update Manager"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ====================== CALENDAR VIEW ======================
  return (
    <div data-testid="sales-pipeline-module" className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-forest-600" /> Sales Pipeline
          </h2>
          {isAdmin && <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">Global view (Admin)</span>}
        </div>
        {!readOnly && (
          <button data-testid="new-deal-btn" onClick={() => openNew(new Date())} className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow">
            <Plus className="w-4 h-4" /> New Deal
          </button>
        )}
      </div>

      {/* Backlog row — pending clients to schedule */}
      {backlogDeals.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl px-4 py-3 shadow-sm" data-testid="backlog-row">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-stone-700 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Backlog · Pending to schedule
              <span className="text-xs font-normal text-stone-400">({backlogDeals.length})</span>
            </h3>
            <span className="text-[11px] text-stone-400">Drag a card onto a day to schedule</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1" data-testid="backlog-list">
            {backlogDeals.map(d => (
              <button
                key={d.id}
                data-testid={`backlog-card-${d.id}`}
                draggable={!readOnly}
                onDragStart={(e) => onDragStart(e, d)}
                onDragEnd={onDragEnd}
                onClick={() => setSelectedDeal(d)}
                className={`flex-shrink-0 w-56 text-left rounded-lg p-2 border transition-shadow hover:shadow-md cursor-grab active:cursor-grabbing ${
                  draggingDeal?.id === d.id ? "opacity-40 border-dashed border-amber-400" : "bg-amber-50/40 border-amber-200"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-stone-800 truncate">{d.prospect_name}</span>
                  <span className="text-xs font-semibold text-forest-700">{formatUSDCompact(d.amount || 0)}</span>
                </div>
                <div className="text-[11px] text-stone-500 truncate">{d.phone || d.email || d.address || "—"}</div>
                <div className="mt-1 flex items-center justify-between">
                  <span className={`px-1.5 py-0 rounded text-[9px] font-semibold ${STATUS_COLORS[d.status] || ""}`}>{d.status}</span>
                  {d.responsible_user_name && <span className="text-[10px] text-stone-400 truncate ml-1">{d.responsible_user_name}</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calendar header */}
      <div className="flex items-center justify-between bg-white border border-stone-200 rounded-xl px-4 py-3 shadow-sm">
        <button data-testid="calendar-prev" onClick={goPrev} className="p-2 rounded-lg hover:bg-stone-100"><ChevronLeft className="w-5 h-5" /></button>
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-stone-800">{monthLabel}</h3>
          <button onClick={goToday} className="text-xs text-forest-600 hover:text-forest-800 font-medium">Today</button>
        </div>
        <button data-testid="calendar-next" onClick={goNext} className="p-2 rounded-lg hover:bg-stone-100"><ChevronRight className="w-5 h-5" /></button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1.5 text-xs font-semibold text-stone-500 px-1">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(d => <div key={d} className="text-center">{d}</div>)}
      </div>

      {/* Calendar grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64 text-stone-500"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (
        <div data-testid="calendar-grid" className="grid grid-cols-7 gap-1.5">
          {grid.map((cell, i) => {
            const dateStr = formatYYYYMMDD(cell.date);
            const isToday = sameDayString(dateStr, new Date());
            const dayDeals = dealsByDay[dateStr] || [];
            return (
              <div
                key={dateStr}
                data-testid={`day-cell-${dateStr}`}
                onDragOver={onDayDragOver}
                onDrop={(e) => onDayDrop(e, dateStr)}
                className={`min-h-[110px] rounded-lg border p-1.5 transition-colors ${
                  cell.inMonth ? "bg-white border-stone-200" : "bg-stone-50/50 border-stone-100 opacity-60"
                } ${isToday ? "ring-2 ring-forest-400" : ""}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={`text-xs font-bold ${isToday ? "text-forest-700" : "text-stone-600"}`}>{cell.date.getDate()}</span>
                  {!readOnly && cell.inMonth && (
                    <button onClick={() => openNew(cell.date)} className="text-stone-300 hover:text-forest-600 p-0.5" title="Add deal">
                      <Plus className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="space-y-1">
                  {dayDeals.map(d => (
                    <button
                      key={d.id}
                      data-testid={`deal-card-${d.id}`}
                      draggable={!readOnly}
                      onDragStart={(e) => onDragStart(e, d)}
                      onDragEnd={onDragEnd}
                      onClick={() => setSelectedDeal(d)}
                      className={`w-full text-left rounded-md p-1.5 text-[11px] border transition-shadow hover:shadow-md cursor-grab active:cursor-grabbing ${
                        draggingDeal?.id === d.id ? "opacity-40 border-dashed border-forest-400" : "bg-forest-50/60 border-forest-200"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-bold text-stone-700">{formatTime12h(d.next_action_time) || "—"}</span>
                        <span className="text-forest-700 font-semibold">{formatUSDCompact(d.amount || 0)}</span>
                      </div>
                      <div className="font-medium text-stone-800 truncate">{d.prospect_name}</div>
                      <div className={`mt-0.5 inline-block px-1.5 py-0 rounded text-[9px] font-semibold ${STATUS_COLORS[d.status] || ""}`}>{d.status}</div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* New Deal Form (overlay) */}
      {showForm && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="new-deal-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-semibold text-stone-800">New Deal</h3>
              <button onClick={() => setShowForm(false)} className="text-stone-400 hover:text-stone-600"><X className="w-5 h-5" /></button>
            </div>
            <BackButton onClick={() => setShowForm(false)} testId="new-deal-back" label="Cancel & back" />
            <form onSubmit={handleSubmit} className="space-y-3 mt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Prospect Name *</label>
                  <input data-testid="new-deal-name" required value={form.prospect_name} onChange={(e) => setForm(f => ({ ...f, prospect_name: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Phone</label>
                  <input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Company</label>
                  <input value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-stone-600 mb-1">Address</label>
                  <input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Amount ($)</label>
                  <CurrencyInput value={form.amount} onChange={(v) => setForm(f => ({ ...f, amount: v }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" data-testid="deal-amount-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Status</label>
                  <select value={form.status} onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm">
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Next Action Date</label>
                  <input type="date" value={form.next_action_date} onChange={(e) => setForm(f => ({ ...f, next_action_date: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" required />
                </div>
                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1">Next Action Time</label>
                  <TimeInput12h value={form.next_action_time} onChange={(v) => setForm(f => ({ ...f, next_action_time: v }))} testId="new-deal-time" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-stone-600 mb-1">Notes</label>
                  <textarea rows={3} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50">Cancel</button>
                <button data-testid="new-deal-save" type="submit" className="bg-forest-600 hover:bg-forest-700 text-white px-5 py-2 rounded-lg text-sm font-semibold">Create Deal</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reschedule Confirm Modal */}
      {confirmReschedule && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reschedule-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
            <h3 className="text-lg font-semibold text-stone-800 mb-1">Reschedule deal?</h3>
            <p className="text-sm text-stone-500 mb-1">Are you sure you want to change the next contact date?</p>
            <p className="text-sm text-stone-700 mb-5">
              <span className="font-medium">{confirmReschedule.deal.prospect_name}</span> · {confirmReschedule.deal.next_action_date} → <span className="font-bold text-forest-700">{confirmReschedule.newDate}</span>
            </p>
            <div className="flex items-center justify-center gap-3">
              <button data-testid="reschedule-cancel" onClick={() => setConfirmReschedule(null)} className="px-5 py-2.5 rounded-lg text-sm font-medium border border-stone-300 text-stone-600 hover:bg-stone-50">Cancel</button>
              <button data-testid="reschedule-confirm" onClick={confirmRescheduleSave} className="px-5 py-2.5 rounded-lg text-sm font-semibold bg-forest-600 hover:bg-forest-700 text-white shadow">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
